import React, {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

import {MarqueeItem, iconPaths, marqueeItems, useTestimonials} from '../../data';

/**
 * 信任背书：合作 logo 跑马灯 + 3 条用户评价。
 * 跑马灯无缝滚动依赖 track 内恰好两组等宽 flex（translateX 0 → -50%）——只能渲染两份，勿改结构。
 */

const renderMarqueeGroup = (groupKey: string): ReactNode => (
  <div className="flex gap-12 items-center flex-shrink-0" aria-hidden={groupKey === 'b' || undefined} key={groupKey}>
    {marqueeItems.map((item: MarqueeItem) => (
      <div
        className={`${
          item.wide ? 'min-w-[9rem] px-3' : 'w-36'
        } h-12 chip-solid rounded-md flex items-center justify-center gap-2 text-slate-500 font-display font-semibold text-sm shadow-sm`}
        key={item.name}
      >
        <img
          alt=""
          aria-hidden="true"
          className={`${item.wide ? item.imgClass || 'h-6 w-auto' : 'w-5 h-5'} flex-shrink-0 ${
            item.grayscale ? 'grayscale' : ''
          }`}
          decoding="async"
          src={item.imgSrc}
        />
        {/* 横向组合标图内自带文字，不再重复显示 name */}
        {!item.wide && <span>{item.name}</span>}
      </div>
    ))}
  </div>
);

const Trust: FC = () => {
  const {t} = useTranslation('home');
  const testimonials = useTestimonials();

  return (
    <section className="relative py-24 lg:py-32 bg-slate-50 overflow-hidden">
      <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-slate-900 mb-4">
            {t('trust.title1')}
            <span className="text-gradient">{t('trust.title2')}</span>
          </h2>
          <p className="text-lg text-slate-500">{t('trust.subtitle')}</p>
        </div>

        {/* Logo 跑马灯 */}
        <div className="relative mb-16 overflow-hidden">
          <div className="flex marquee-track gap-12 items-center">
            {renderMarqueeGroup('a')}
            {renderMarqueeGroup('b')}
          </div>
        </div>

        {/* 用户评价 */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div
              className="glass-card rounded-lg p-6 scroll-reveal shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
              key={testimonial.key}
              style={{transitionDelay: testimonial.transitionDelay}}
            >
              <div className="flex items-center gap-1 mb-4">
                {[0, 1, 2, 3, 4].map((star) => (
                  <svg
                    className="w-4 h-4 text-amber-400"
                    fill="none"
                    key={star}
                    stroke="currentColor"
                    strokeWidth={1.5}
                    viewBox="0 0 24 24"
                  >
                    <path d={iconPaths.star} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">&quot;{testimonial.quote}&quot;</p>
              <div className="flex items-center gap-3">
                <img
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover object-top"
                  decoding="async"
                  loading="lazy"
                  src={testimonial.avatarSrc}
                />
                <div>
                  <div className="text-slate-900 text-sm font-medium">{testimonial.name}</div>
                  <div className="text-slate-500 text-xs">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Trust;
